import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Area, AreaChart } from 'recharts';
import { LogEntry } from '../../types';
interface ChartsPanelProps {
  logs: LogEntry[];
}
export function ChartsPanel({
  logs
}: ChartsPanelProps) {
  // Process data for Area Chart (Logs per minute)
  const lineChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    logs.forEach(log => {
      const date = new Date(log.timestamp);
      const key = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([time, count]) => ({
      time,
      count
    })).sort((a, b) => a.time.localeCompare(b.time)).slice(-20);
  }, [logs]);
  // Process data for Pie Chart (Status distribution)
  const pieChartData = useMemo(() => {
    let success = 0,
      error = 0,
      warning = 0;
    logs.forEach(log => {
      if (log.statusCode >= 500) error++;else if (log.statusCode >= 400) warning++;else if (log.statusCode >= 200 && log.statusCode < 300) success++;
    });
    return [{
      name: 'Success (2xx)',
      value: success,
      color: '#22c55e'
    }, {
      name: 'Client Error (4xx)',
      value: warning,
      color: '#eab308'
    }, {
      name: 'Server Error (5xx)',
      value: error,
      color: '#ef4444'
    }].filter(d => d.value > 0);
  }, [logs]);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-card border border-border p-3 rounded-lg shadow-xl" data-id="element-3">
          <p className="text-sm font-medium text-foreground mb-1" data-id="element-4">{label}</p>
          <p className="text-sm" style={{
          color: '#eb5b0b'
        }} data-id="element-5">
            <span className="font-semibold" data-id="element-6">{payload[0].value}</span> logs
          </p>
        </div>;
    }
    return null;
  };
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-7">
      <Card className="lg:col-span-2 border-gray-200 bg-white shadow-sm" data-id="element-8">
        <CardHeader className="pb-2" data-id="element-9">
          <CardTitle className="text-sm font-medium text-muted-foreground" data-id="element-10">
            Volumen de Logs (Últimos 20 min)
          </CardTitle>
        </CardHeader>
        <CardContent data-id="element-11">
          <div className="h-[250px] w-full mt-4" data-id="element-12">
            {lineChartData.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-13">
                <AreaChart data={lineChartData} margin={{
              top: 5,
              right: 20,
              bottom: 5,
              left: 0
            }} data-id="element-14">
                  <defs data-id="element-15">
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1" data-id="element-16">
                      <stop offset="5%" stopColor="#034076" stopOpacity={0.15} data-id="element-17" />
                      <stop offset="95%" stopColor="#034076" stopOpacity={0} data-id="element-18" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-19" />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} dy={10} data-id="element-20" />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} dx={-10} allowDecimals={false} data-id="element-21" />
                  <Tooltip content={<CustomTooltip data-id="element-23" />} data-id="element-22" />
                  <Area type="monotone" dataKey="count" stroke="#eb5b0b" strokeWidth={2} fill="url(#colorCount)" dot={false} activeDot={{
                r: 5,
                fill: '#eb5b0b',
                stroke: '#ffffff',
                strokeWidth: 2
              }} data-id="element-24" />
                </AreaChart>
              </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-id="element-25">
                Esperando datos de logs...
              </div>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 bg-white shadow-sm" data-id="element-26">
        <CardHeader className="pb-2" data-id="element-27">
          <CardTitle className="text-sm font-medium text-muted-foreground" data-id="element-28">
            Distribución de Estados
          </CardTitle>
        </CardHeader>
        <CardContent data-id="element-29">
          <div className="h-[250px] w-full flex items-center justify-center" data-id="element-30">
            {pieChartData.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-31">
                <PieChart data-id="element-32">
                  <Pie data={pieChartData} cx="50%" cy="45%" innerRadius={55} outerRadius={78} paddingAngle={4} dataKey="value" stroke="none" data-id="element-33">
                    {pieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-34" />)}
                  </Pie>
                  <Tooltip contentStyle={{
                backgroundColor: '#ffffff',
                borderColor: '#e5e7eb',
                borderRadius: '0.5rem',
                color: '#1f2937'
              }} itemStyle={{
                color: '#1f2937'
              }} data-id="element-35" />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} formatter={(value: string) => <span style={{
                color: '#6b7280',
                fontSize: '11px'
              }} data-id="element-37">
                        {value}
                      </span>} data-id="element-36" />
                </PieChart>
              </ResponsiveContainer> : <div className="text-sm text-muted-foreground" data-id="element-38">
                Sin datos disponibles
              </div>}
          </div>
        </CardContent>
      </Card>
    </div>;
}